import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api, { type HsiCapture } from '../api/client';
import axios from 'axios';

const PINNED_COLUMNS: Array<{ key: string; label: string }> = [
    { key: 'capture_id', label: 'capture_id' },
    { key: 'experiment_id', label: 'experiment' },
    { key: 'fish_id', label: 'fish_id' },
    { key: 'nera_assessment_ids', label: 'NERA IDs' },
    { key: 'fish_part', label: 'part' },
    { key: 'days_since_kill', label: 'days' },
    { key: 'taste_score', label: 'taste' },
    { key: 'odor', label: 'odor' },
    { key: 'tags', label: 'tags' },
  ];

  function cellValue(row: HsiCapture, key: string): string {
    if (key === 'nera_assessment_ids') {
      const ids = row.nera_assessment_ids;
      return Array.isArray(ids) && ids.length > 0
        ? ids.join(', ')
        : '—';
    }
    if (key === 'taste_score') {
      return formatAvg(row[key]);
    }
    if (key === 'organoleptic') {
      const sum = organolepticSum(row.organoleptic);
      return sum == null ? '—' : String(sum);
    }
    const v = row[key];
    if (v === null || v === undefined) return '—';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  }

type ScoreMap = Record<string, number>;

function asScoreMap(v: unknown): ScoreMap {
  if (v == null) return {};
  if (typeof v === 'number' && Number.isFinite(v)) return { unknown: v };
  if (typeof v === 'object' && !Array.isArray(v)) {
    const out: ScoreMap = {};
    for (const [k, raw] of Object.entries(v as Record<string, unknown>)) {
      const n = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isFinite(n)) out[k] = n;
    }
    return out;
  }
  return {};
}

function scoreAvg(v: unknown): number | null {
  const m = asScoreMap(v);
  const vals = Object.values(m);
  if (!vals.length) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function formatAvg(v: unknown): string {
  const avg = scoreAvg(v);
  return avg == null ? '—' : avg.toFixed(2);
}
function organolepticSum(v: unknown): number | null {
  if (v == null || typeof v !== 'object' || Array.isArray(v)) return null;

  let sum = 0;
  let found = false;

  const walk = (node: unknown) => {
    if (typeof node === 'number' && Number.isFinite(node)) {
      sum += node;
      found = true;
      return;
    }
    if (node != null && typeof node === 'object' && !Array.isArray(node)) {
      for (const child of Object.values(node as Record<string, unknown>)) {
        walk(child);
      }
    }
  };

  walk(v);
  return found ? sum : null;
}

function buildColumns(captures: HsiCapture[]): Array<{ key: string; label: string }> {
    const pinnedKeys = new Set(PINNED_COLUMNS.map((c) => c.key));
    const extras = new Set<string>();
  
    for (const row of captures) {
      for (const key of Object.keys(row)) {
        if (pinnedKeys.has(key)) continue;
        const v = row[key];
        if (v !== null && typeof v === 'object') continue;
        extras.add(key);
      }
    }
  
    const extraCols = Array.from(extras)
      .sort()
      .map((key) => ({ key, label: key }));
    return [...PINNED_COLUMNS, ...extraCols];
  }

  function pcaS3UriFromCapture(capture: HsiCapture): string | null {
    const uri = capture.s3_uri;
    if (typeof uri === 'string' && uri.startsWith('s3://')) {
      return `${uri.replace(/\/?$/, '')}/renders/pca_rgb.png`;
    }
    const bucket = capture.s3_bucket;
    const prefix = capture.s3_prefix;
    if (typeof bucket === 'string' && typeof prefix === 'string') {
      return `s3://${bucket}/${String(prefix).replace(/\/?$/, '')}/renders/pca_rgb.png`;
    }
    return null;
  }

  type Props = {
    onOpenNeraAssessment?: (target: {
      assessmentId: number;
      date: string;
    }) => void;
  };

  function renderNeraCell(
    row: HsiCapture,
    onOpen?: Props['onOpenNeraAssessment'],
  ) {
    const refs = row.nera_assessments;
    if (!Array.isArray(refs) || refs.length === 0) return '—';
  
    return (
      <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
        {refs.map((ref, i) => {
          const date = ref.collection_date?.slice(0, 10) ?? null;
          const canLink = Boolean(onOpen && date);
          return (
            <React.Fragment key={ref.id}>
              {i > 0 ? <span>, </span> : null}
              {canLink ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen!({ assessmentId: ref.id, date: date! });
                  }}
                  style={{
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    color: '#0d6efd',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                  title={`Open NERA assessment ${ref.id} on ${date}`}
                >
                  {ref.id}
                </button>
              ) : (
                <span title={date ? undefined : 'No collection_date; cannot deep-link'}>
                  {ref.id}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </span>
    );
  }

  
  

  const PAGE_SIZE = 50;

const HsiViewer: React.FC<Props> = ({ onOpenNeraAssessment }) => {
  const [experimentId, setExperimentId] = useState('');
  const [species, setSpecies] = useState('');
  const [allCaptures, setAllCaptures] = useState<HsiCapture[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<HsiCapture | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [attrKey, setAttrKey] = useState('');
  const [attrValue, setAttrValue] = useState('');
  const [saving, setSaving] = useState(false);
  type PreviewView = 'rgb' | 'hsi';
  const [previewView, setPreviewView] = useState<PreviewView>('rgb');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMessage, setPreviewMessage] = useState('');
  const [inputter, setInputter] = useState('');
  const [tasteScore, setTasteScore] = useState<string>('');
  const [savingSensory, setSavingSensory] = useState(false);
  const [showPrevScores, setShowPrevScores] = useState(false);
  const [selectedCaptureIds, setSelectedCaptureIds] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(-1);
  const [tagInput, setTagInput] = useState('');
  const [tagToRemove, setTagToRemove] = useState('');
  const [addingTags, setAddingTags] = useState(false);
  const [removingTag, setRemovingTag] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: { experiment_id?: string; species?: string } = {};
      if (experimentId.trim()) params.experiment_id = experimentId.trim();
      else if (species.trim()) params.species = species.trim();
  
      const res = await api.hsi.listCaptures(params);
      setAllCaptures(res.data.captures || []);
      setCurrentPage(1);
      setSelectedCaptureIds(new Set());
      setLastSelectedIndex(-1);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load captures');
      setAllCaptures([]);
    } finally {
      setLoading(false);
    }
  }, [experimentId, species]);

  const totalPages = Math.max(1, Math.ceil(allCaptures.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, allCaptures.length);
  const visibleCaptures = allCaptures.slice(startIndex, endIndex);
  
  const goToPage = (page: number) => {
    const last = Math.max(1, totalPages);
    if (page >= 1 && page <= last) setCurrentPage(page);
  };
  
  useEffect(() => {
    setCurrentPage((p) => Math.min(p, totalPages));
  }, [totalPages]);
  
  const columns = useMemo(() => buildColumns(allCaptures), [allCaptures]);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(
    () => new Set(PINNED_COLUMNS.map((c) => c.key))
  );
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  
  const visibleColumns = useMemo(
    () => columns.filter((c) => visibleKeys.has(c.key)),
    [columns, visibleKeys]
  );

  function normalizeTags(raw: unknown): string[] {
    if (!Array.isArray(raw)) return [];
    return raw.map(String).map((t) => t.trim()).filter(Boolean);
  }
  
  const selectedTagsUnion = useMemo(() => {
    const set = new Set<string>();
    for (const row of allCaptures) {
      if (!selectedCaptureIds.has(row.capture_id)) continue;
      normalizeTags(row.tags).forEach((t) => set.add(t));
    }
    return Array.from(set).sort();
  }, [allCaptures, selectedCaptureIds]);
  
  const suggestedTags = useMemo(() => {
    const set = new Set<string>();
    for (const row of allCaptures) {
      normalizeTags(row.tags).forEach((t) => set.add(t));
    }
    return Array.from(set).sort();
  }, [allCaptures]);

  const handleRowSelect = (
    captureId: string,
    indexInAll: number, // index in allCaptures (or filtered list if you add filters)
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    const next = new Set(selectedCaptureIds);
  
    if (event.shiftKey && lastSelectedIndex >= 0) {
      const start = Math.min(lastSelectedIndex, indexInAll);
      const end = Math.max(lastSelectedIndex, indexInAll);
      for (let i = start; i <= end; i++) {
        next.add(allCaptures[i].capture_id);
      }
    } else {
      if (next.has(captureId)) next.delete(captureId);
      else next.add(captureId);
      setLastSelectedIndex(indexInAll);
    }
    setSelectedCaptureIds(next);
  };
  
  const selectAll = useCallback(() => {
    setSelectedCaptureIds(new Set(allCaptures.map((c) => c.capture_id)));
    setLastSelectedIndex(allCaptures.length - 1);
  }, [allCaptures]);
  
  const deselectAll = useCallback(() => {
    setSelectedCaptureIds(new Set());
    setLastSelectedIndex(-1);
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'a') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (allCaptures.length === 0) return;
      e.preventDefault();
      selectAll();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [allCaptures, selectAll]);

  const applyLocalTagUpdate = (ids: string[], mutator: (tags: string[]) => string[]) => {
    setAllCaptures((prev) =>
      prev.map((c) =>
        ids.includes(c.capture_id)
          ? { ...c, tags: mutator(normalizeTags(c.tags)) }
          : c
      )
    );
    setSelected((prev) =>
      prev && ids.includes(prev.capture_id)
        ? { ...prev, tags: mutator(normalizeTags(prev.tags)) }
        : prev
    );
  };
  
  const handleAddTags = async () => {
    const tags = tagInput.split(/\s*,\s*/).map((s) => s.trim()).filter(Boolean);
    if (!tags.length || selectedCaptureIds.size === 0) return;
    setAddingTags(true);
    setError('');
    try {
      const ids = Array.from(selectedCaptureIds);
      const res = await api.hsi.addTags({ capture_ids: ids, tags });
      applyLocalTagUpdate(ids, (current) => {
        const set = new Set(current);
        tags.forEach((t) => set.add(t));
        return Array.from(set);
      });
      setTagInput('');
      if (res.data.errors?.length) {
        setError(`Add tags: ${res.data.successful_updates} updated; ${res.data.errors.join('; ')}`);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to add tags');
    } finally {
      setAddingTags(false);
    }
  };
  
  const handleRemoveTag = async () => {
    const tag = tagToRemove.trim();
    if (!tag || selectedCaptureIds.size === 0) return;
    setRemovingTag(true);
    setError('');
    try {
      const ids = Array.from(selectedCaptureIds);
      const res = await api.hsi.removeTag({ capture_ids: ids, tag });
      applyLocalTagUpdate(ids, (current) => current.filter((t) => t !== tag));
      setTagToRemove('');
      if (res.data.errors?.length) {
        setError(`Remove tag: ${res.data.successful_updates} updated; ${res.data.errors.join('; ')}`);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to remove tag');
    } finally {
      setRemovingTag(false);
    }
  };
  
  const toggleColumn = (key: string) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        // Keep at least one column (and prefer keeping capture_id)
        if (key === 'capture_id' || next.size <= 1) return prev;
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  useEffect(() => {
    loadList();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- initial load only
  
  useEffect(() => {
    if (!selectedId) {
      setPreviewUrl((prev) => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return null;
      });
      setPreviewMessage('');
      setPreviewLoading(false);
      return;
    }
  
    let cancelled = false;
    let objectUrl: string | null = null;
  
    const run = async () => {
      setPreviewLoading(true);
      setPreviewMessage('');
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
  
      try {
        if (previewView === 'hsi') {
          if (!selected || selected.capture_id !== selectedId) {
            return;
          }
        
          const s3Uri = pcaS3UriFromCapture(selected);
          if (!s3Uri) {
            setPreviewMessage('Capture has no s3_uri; cannot load PCA render.');
            return;
          }
        
          const { data } = await api.dataviewer.getPresignedUrls([s3Uri]);
          const item = data.urls?.[0];
          const signed = item?.presigned_url;
          if (!signed || item?.error) {
            setPreviewMessage(
              item?.error
                ? `Failed to presign PCA render: ${item.error}`
                : 'No PCA render for this capture.'
            );
            return;
          }
        
          // Do NOT fetch() — S3 CORS will block localhost. Let <img> load it.
          setPreviewUrl(signed);
          return;
        }
  
        // RGB path (unchanged)
        const res = await api.hsi.getPreview(selectedId, 'rgb');
        if (cancelled) return;
  
        if (res.status === 501) {
          setPreviewMessage(
            (res.data as { detail?: string })?.detail ||
              'HSI preview is not implemented yet.'
          );
          return;
        }
  
        objectUrl = URL.createObjectURL(res.data as Blob);
        setPreviewUrl(objectUrl);
      } catch (e: unknown) {
        if (cancelled) return;
        const detail = axios.isAxiosError(e)
          ? (e.response?.data as { detail?: string } | undefined)?.detail || e.message
          : e instanceof Error
            ? e.message
            : 'Failed to load preview';
        setPreviewMessage(
          previewView === 'hsi' ? 'No PCA render for this capture.' : String(detail)
        );
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
  
    void run();
  
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selectedId, previewView, selected]);

  const openCapture = async (captureId: string) => {
    setSelectedId(captureId);
    setDetailLoading(true);
    setError('');
    try {
      const res = await api.hsi.getCapture(captureId);
      setSelected(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load capture');
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const selectedCaptureId = selected?.capture_id ?? null;
  
  useEffect(() => {
    if (selectedCaptureId == null) {
      setTasteScore('');
      setShowPrevScores(false);
      return;
    }
    setTasteScore('');
  }, [selectedCaptureId]);

  const saveSensory = async () => {
    if (!selectedId || !selected) return;
  
    const name = inputter.trim();
    if (!name) {
      setError('Enter inputter name');
      return;
    }
  
    const body: { inputter: string; taste_score?: number } = {
      inputter: name,
    };
  
    if (tasteScore.trim() !== '') {
      const n = Number(tasteScore);
      if (!Number.isFinite(n) || n < 1 || n > 10) {
        setError('taste_score must be a number 1.00-10.00');
        return;
      }
      body.taste_score = n;
    }
    if (body.taste_score == null) {
      setError('Enter taste_score');
      return;
    }
  
    setSavingSensory(true);
    setError('');
    try {
      await api.hsi.patchSensory(selectedId, body);
      await openCapture(selectedId);
      await loadList();
      setInputter('');
      setTasteScore('');
    } catch (e: unknown) {
      const detail = axios.isAxiosError(e)
        ? (e.response?.data as { detail?: string } | undefined)?.detail || e.message
        : e instanceof Error
          ? e.message
          : 'Failed to save sensory scores';
      setError(detail);
    } finally {
      setSavingSensory(false);
    }
  };

  const saveAttribute = async () => {
    if (!selectedId || !attrKey.trim()) return;
    setSaving(true);
    setError('');
    try {
      let value: unknown = attrValue;
      const trimmed = attrValue.trim();
      if (trimmed === 'true') value = true;
      else if (trimmed === 'false') value = false;
      else if (trimmed !== '' && !Number.isNaN(Number(trimmed))) value = Number(trimmed);
      else {
        try {
          value = JSON.parse(trimmed);
        } catch {
          value = attrValue;
        }
      }

      const res = await api.hsi.patchCapture(selectedId, { [attrKey.trim()]: value });
      setSelected(res.data);
      setAttrKey('');
      setAttrValue('');
      await loadList();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>HSI Viewer</h2>
      <p style={{ color: '#666', maxWidth: 720 }}>
        Catalog of hyperspectral captures from DynamoDB (<code>hsi-captures</code>).
        Add arbitrary top-level attributes below; imaging renders come later.
      </p>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: '#666' }}>experiment_id</span>
          <input
            value={experimentId}
            onChange={(e) => setExperimentId(e.target.value)}
            placeholder="exp-nickbark"
            style={{ padding: '0.4rem 0.6rem', minWidth: 180 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: '#666' }}>species (if no experiment)</span>
          <input
            value={species}
            onChange={(e) => setSpecies(e.target.value)}
            placeholder="SBL"
            style={{ padding: '0.4rem 0.6rem', minWidth: 120 }}
          />
        </label>
        <button
          type="button"
          onClick={loadList}
          disabled={loading}
          style={{
            padding: '0.5rem 1rem',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <div style={{ marginBottom: '1rem' }}>
  <button
    type="button"
    onClick={() => setShowColumnPicker((v) => !v)}
    style={{
      padding: '0.4rem 0.8rem',
      border: '1px solid #dee2e6',
      borderRadius: 4,
      background: '#fff',
      cursor: 'pointer',
    }}
  >
    Columns ({visibleColumns.length}/{columns.length})
  </button>

  {showColumnPicker && (
    <div
      style={{
        marginTop: 8,
        padding: '0.75rem',
        border: '1px solid #dee2e6',
        borderRadius: 4,
        background: '#f8f9fa',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem 1rem',
        maxWidth: 900,
      }}
    >
      <div style={{ width: '100%', display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => setVisibleKeys(new Set(columns.map((c) => c.key)))}
          style={{ fontSize: 12, cursor: 'pointer' }}
        >
          Show all
        </button>
        <button
          type="button"
          onClick={() => setVisibleKeys(new Set(PINNED_COLUMNS.map((c) => c.key)))}
          style={{ fontSize: 12, cursor: 'pointer' }}
        >
          Default
        </button>
      </div>

      {columns.map((c) => (
        <label
          key={c.key}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
        >
          <input
            type="checkbox"
            checked={visibleKeys.has(c.key)}
            disabled={c.key === 'capture_id'}
            onChange={() => toggleColumn(c.key)}
          />
          {c.label}
        </label>
      ))}
    </div>
  )}
</div>

{selectedCaptureIds.size > 0 && (
  <div style={{
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '0.75rem',
  }}>
    <span style={{ fontSize: 13, color: '#666', marginRight: 'auto' }}>
      {selectedCaptureIds.size} selected
    </span>

    <button type="button" onClick={selectAll}>Select all ({allCaptures.length})</button>
    <button type="button" onClick={deselectAll}>Deselect all</button>

    <input
      value={tagInput}
      onChange={(e) => setTagInput(e.target.value)}
      placeholder="Tags to add (comma-separated)"
      list="hsi-tag-suggestions"
      onKeyDown={(e) => e.key === 'Enter' && handleAddTags()}
      style={{ padding: '0.5rem 0.75rem', border: '1px solid #dee2e6', borderRadius: 4, width: 220 }}
    />
    <datalist id="hsi-tag-suggestions">
      {suggestedTags.map((t) => (
        <option key={t} value={t} />
      ))}
    </datalist>
    <button
      type="button"
      onClick={handleAddTags}
      disabled={addingTags || !tagInput.trim()}
    >
      {addingTags ? 'Adding...' : `Add tags (${selectedCaptureIds.size})`}
    </button>

    <select
      value={tagToRemove}
      onChange={(e) => setTagToRemove(e.target.value)}
      disabled={selectedTagsUnion.length === 0}
    >
      <option value="">
        {selectedTagsUnion.length === 0 ? 'No tags on selected' : 'Tag to remove...'}
      </option>
      {selectedTagsUnion.map((t) => (
        <option key={t} value={t}>{t}</option>
      ))}
    </select>
    <button
      type="button"
      onClick={handleRemoveTag}
      disabled={removingTag || !tagToRemove.trim()}
    >
      {removingTag ? 'Removing...' : 'Remove tag'}
    </button>
  </div>
)}

      {error && (
        <div style={{ color: '#b00020', marginBottom: '1rem', whiteSpace: 'pre-wrap' }}>{error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: selectedId ? '1.2fr 1fr' : '1fr', gap: '1.5rem' }}>
        <div style={{ overflowX: 'auto', border: '1px solid #dee2e6', borderRadius: 4 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f8f9fa', textAlign: 'left' }}>
              <th style={{ width: 36, padding: '0.5rem 0.75rem', borderBottom: '1px solid #dee2e6' }} />
              {visibleColumns.map((c) => (
                <th key={c.key} style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid #dee2e6' }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
            <tbody>
            {visibleCaptures.map((row, pageIdx) => {
              const absIndex = startIndex + pageIdx;
              const isChecked = selectedCaptureIds.has(row.capture_id);
              return (
                  <tr
                    key={row.capture_id}
                    onClick={() => openCapture(row.capture_id)}
                    style={{
                      cursor: 'pointer',
                      background:
                        selectedId === row.capture_id
                          ? '#e7f1ff'
                          : isChecked
                            ? '#f0f7ff'
                            : undefined,
                    }}
                  >
                    <td
                      onClick={(e) => handleRowSelect(row.capture_id, absIndex, e)}
                      style={{ padding: '0.5rem 0.75rem' }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        readOnly
                        style={{ pointerEvents: 'none' }}
                        tabIndex={-1}
                      />
                    </td>
                    {visibleColumns.map((c) => (
                    <td
                    key={c.key}
                    style={{
                        padding: '0.5rem 0.75rem',
                        borderBottom: '1px solid #f1f3f5',
                        maxWidth: 220,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                    >
                    {c.key === 'nera_assessment_ids'
                      ? renderNeraCell(row, onOpenNeraAssessment)
                      : c.key === 'tags'
                        ? (normalizeTags(row.tags).join(', ') || '—')
                        : cellValue(row, c.key)}
                    </td>
                ))}
                </tr>
                );
              })}
              {visibleCaptures.length === 0 && !loading && (
                <tr>
                  <td colSpan={visibleColumns.length + 1} style={{ padding: '1rem', color: '#666' }}>
                    No captures found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {selectedId && (
          <div style={{ border: '1px solid #dee2e6', borderRadius: 4, padding: '1rem' }}>
            <h3 style={{ marginTop: 0 }}>Capture detail</h3>
            {detailLoading && <p>Loading…</p>}
            <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <button
                    type="button"
                    onClick={() => setPreviewView('rgb')}
                    style={{
                        padding: '0.35rem 0.75rem',
                        border: '1px solid #dee2e6',
                        borderRadius: 4,
                        background: previewView === 'rgb' ? '#007bff' : '#fff',
                        color: previewView === 'rgb' ? '#fff' : '#333',
                        cursor: 'pointer',
                    }}
                    >
                    RGB
                    </button>
                    <button
                    type="button"
                    onClick={() => setPreviewView('hsi')}
                    style={{
                        padding: '0.35rem 0.75rem',
                        border: '1px solid #dee2e6',
                        borderRadius: 4,
                        background: previewView === 'hsi' ? '#007bff' : '#fff',
                        color: previewView === 'hsi' ? '#fff' : '#333',
                        cursor: 'pointer',
                    }}
                    >
                    HSI
                    </button>
                </div>

                {previewLoading && <p style={{ color: '#666' }}>Loading preview…</p>}
                {previewMessage && (
                  <p style={{ color: previewView === 'hsi' ? '#666' : '#b00020' }}>
                    {previewMessage}
                  </p>
                )}
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt={previewView === 'hsi' ? `HSI PCA ${selectedId}` : `HSI RGB ${selectedId}`}
                    onError={() => {
                      setPreviewUrl(null);
                      setPreviewMessage('No PCA render for this capture.');
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      height: 'auto',
                      maxHeight: 480,
                      objectFit: 'contain',
                    }}
                  />
                )}
                </div>
            {selected && (
              <>
                
                <h4>Sensory scores</h4>
                <p style={{ fontSize: 12, color: '#666', marginTop: 0 }}>
                  Per fish in this experiment (fish_id={String(selected.fish_id ?? '—')}).
                  Saving upserts this inputter’s scores on all captures for this fish.
                </p>

                {/* Current maps + averages (hidden by default to avoid biasing the inputter) */}
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12,
                      color: '#666',
                      marginBottom: 8,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={showPrevScores}
                      onChange={(e) => setShowPrevScores(e.target.checked)}
                    />
                    Show previous scores
                  </label>

                  {showPrevScores && (
                    <div style={{ fontSize: 12, marginBottom: 8 }}>
                      <div>
                        taste avg: <strong>{formatAvg(selected.taste_score)}</strong>{' '}
                        <code>{JSON.stringify(asScoreMap(selected.taste_score))}</code>
                      </div>
                    </div>
                  )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#666' }}>
                    inputter
                    <input
                      value={inputter}
                      onChange={(e) => setInputter(e.target.value)}
                      placeholder="your name"
                      style={{ padding: '0.4rem 0.6rem' }}
                    />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#666' }}>
                    taste_score
                    <input
                      type="number"
                      min={1}
                      max={10}
                      step={0.1}
                      value={tasteScore}
                      onChange={(e) => setTasteScore(e.target.value)}
                      placeholder="1–10"
                      style={{ padding: '0.4rem 0.6rem' }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={saveSensory}
                    disabled={savingSensory || !selected.experiment_id || selected.fish_id == null}
                    style={{
                      padding: '0.5rem 1rem',
                      alignSelf: 'flex-start',
                      background: '#007bff',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                  >
                    {savingSensory ? 'Saving…' : 'Save sensory'}
                  </button>
                </div>

                <h4>Add / set attribute</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input
                    value={attrKey}
                    onChange={(e) => setAttrKey(e.target.value)}
                    placeholder="attribute key (e.g. reviewer)"
                    style={{ padding: '0.4rem 0.6rem' }}
                  />
                  <input
                    value={attrValue}
                    onChange={(e) => setAttrValue(e.target.value)}
                    placeholder='value (string, number, true/false, or JSON)'
                    style={{ padding: '0.4rem 0.6rem' }}
                  />
                  <button
                    type="button"
                    onClick={saveAttribute}
                    disabled={saving || !attrKey.trim()}
                    style={{
                      padding: '0.5rem 1rem',
                      alignSelf: 'flex-start',
                      background: '#28a745',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                    }}
                  >
                    {saving ? 'Saving…' : 'Save attribute'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {totalPages > 1 && (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', marginTop: '1.25rem' }}>
        <button type="button" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} style={{
          padding: '0.4rem 1rem',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          background: currentPage === 1 ? '#e9ecef' : '#fff',
          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
          fontSize: '0.9rem',
        }}>
          Previous
        </button>
        <span style={{ fontSize: '0.9rem', color: '#555' }}>
          Page {currentPage} of {totalPages} ({startIndex + 1}–{endIndex} of {allCaptures.length})
        </span>
        <button type="button" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} style={{
          padding: '0.4rem 1rem',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          background: currentPage === totalPages ? '#e9ecef' : '#fff',
          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
          fontSize: '0.9rem',
        }}>
          Next
        </button>
      </div>
    )}
    </div>
  );
};

export default HsiViewer;